// EndpointModal.tsx
import { Modal, Form, Input, Button, Space, Select, Tabs, Typography, Card, Table, Tag, Tooltip, message } from 'antd';
import { ApiOutlined, NotificationOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSuperAdmin } from '../../../contexts/SuperAdminContext';
import { useEffect, useState } from 'react';
import { NotificationConfigModal } from './NotificationConfigModal';
import { useQueryClient } from '@tanstack/react-query';

interface EndpointFormValues {
  id: string;
  name: string;
  baseUrl: string;
  token: string;
  userId: string;
  celular?: string;
  status: string;
  gamificationCustomFieldId?: string;
}

interface EndpointModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: {
    id: string;
    name: string;
    baseUrl: string;
    token: string;
    userId: string;
    celular?: string;
    status: string;
    gamificationCustomFieldId?: string;
    adminId?: string; // For pre-selecting admin when creating from Entity Management
  };
}

export const EndpointModal = ({ open, onClose, mode, initialData }: EndpointModalProps) => {
  const [form] = Form.useForm<EndpointFormValues>();
  const { users, createEndpoint, editEndpoint, deleteNotificationConfig, getNotificationConfigsByEndpoint } = useSuperAdmin();
  const [activeTab, setActiveTab] = useState('basic');
  const [notificationConfigs, setNotificationConfigs] = useState([]);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationModalMode, setNotificationModalMode] = useState<'create' | 'edit'>('create');
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      form.setFieldsValue(initialData);
      loadNotificationConfigs(initialData.id);
    } else {
      form.resetFields();
      form.setFieldsValue({ status: 'Active' }); // Set default status for new endpoints
      setNotificationConfigs([]);
      
      // Pre-seleccionar admin específico cuando se crea desde Entity Management
      if (mode === 'create' && initialData?.adminId) {
        form.setFieldsValue({
          status: 'Active',
          userId: initialData.adminId
        });
      }
    }
  }, [form, initialData, mode]);

  // Efecto adicional para recargar notificaciones cuando se cierre el modal de notificaciones
  useEffect(() => {
    if (!notificationModalOpen && mode === 'edit' && initialData?.id) {
      // El modal de notificaciones se cerró, forzar recarga
      const reloadConfigs = async () => {
        try {
          const configs = await getNotificationConfigsByEndpoint(initialData.id);
          setNotificationConfigs(configs);
        } catch (error) {
          console.error('[EndpointModal] Error force reloading configs:', error);
        }
      };
      
      // Pequeño delay para asegurar sincronización
      setTimeout(reloadConfigs, 200);
    }
  }, [notificationModalOpen, mode, initialData?.id, getNotificationConfigsByEndpoint]);

  const loadNotificationConfigs = async (endpointId: string) => {
    if (!endpointId) {
      return;
    }
    
    setLoading(true);
    try {
      const configs = await getNotificationConfigsByEndpoint(endpointId);
      setNotificationConfigs(configs || []);
      
      // Forzar re-render de la tabla
      setTimeout(() => {
      }, 100);
    } catch (error) {
      console.error('[EndpointModal] Error loading notification configs:', error);
      setNotificationConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: EndpointFormValues) => {
    try {
      if (mode === 'edit' && initialData) {
        await editEndpoint({
          id: initialData.id,
          data: values,
        });
        message.success('Endpoint actualizado correctamente');
      } else {
        await createEndpoint(values);
        message.success('Endpoint creado correctamente');
      }
      
      // Invalidar queries para actualizar la tabla principal
      queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
      
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error saving endpoint:', error);
      message.error('Error al guardar el endpoint');
    }
  };

  const handleCreateNotification = () => {
    setNotificationModalMode('create');
    setSelectedConfig(null);
    setNotificationModalOpen(true);
  };

  const handleEditNotification = (config: any) => {
    setNotificationModalMode('edit');
    setSelectedConfig(config);
    setNotificationModalOpen(true);
  };

  const handleDeleteNotification = async (configId: string) => {
    try {
      await deleteNotificationConfig(configId);
      
      // Recargar configuraciones y invalidar queries relacionadas
      if (initialData?.id) {
        await loadNotificationConfigs(initialData.id);
      }
      
      // Invalidar queries globales de notificaciones
      queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
      message.success('Configuración de notificación eliminada correctamente');
    } catch (error) {
      console.error('Error deleting notification config:', error);
      message.error('Error al eliminar la configuración de notificación');
    }
  };

  const handleNotificationModalClose = async () => {
    setNotificationModalOpen(false);
    
    if (initialData?.id) {
      // Forzar recarga inmediata de configuraciones
      setLoading(true);
      try {
        // Pequeño delay para asegurar que el backend termine de procesar
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Recargar configuraciones del endpoint actual
        const configs = await getNotificationConfigsByEndpoint(initialData.id);
        setNotificationConfigs(configs);
      } catch (error) {
        console.error('Error reloading notification configs:', error);
      } finally {
        setLoading(false);
      }
    }
    
    // Invalidar queries globales para asegurar sincronización
    queryClient.invalidateQueries({ queryKey: ["notificationConfigs"] });
    queryClient.invalidateQueries({ queryKey: ["panelEndpoints"] });
  };

  const notificationColumns = [
    {
      title: 'Tipo',
      dataIndex: 'notificationType',
      key: 'notificationType',
      render: (type: string) => {
        const typeMap: Record<string, { color: string, text: string }> = {
          'DEPOSIT_COMPLETED': { color: 'green', text: 'Carga completada' },
          'DEPOSIT_GAMIFIED': { color: 'purple', text: 'Carga gamificada' },
          'DEPOSIT_VALIDATION': { color: 'blue', text: 'Carga requiere validación' },
          'DEPOSIT_REJECTION': { color: 'red', text: 'Carga rechazada' },
          'WITHDRAW_APPROVAL': { color: 'cyan', text: 'Retiro aprobado' },
          'WITHDRAW_REJECTION': { color: 'volcano', text: 'Retiro rechazado' },
          'WITHDRAW_COMPLETED': { color: 'green', text: 'Retiro completado' },
          'USER_CREATED': { color: 'geekblue', text: 'Usuario creado' },
          'USER_REJECTED': { color: 'magenta', text: 'Usuario rechazado' }
        };
        
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Usa Flujo',
      dataIndex: 'useFlow',
      key: 'useFlow',
      render: (useFlow: boolean) => (
        <Tag color={useFlow ? 'green' : 'orange'}>
          {useFlow ? 'Sí' : 'No'}
        </Tag>
      )
    },
    {
      title: 'ID Flujo',
      dataIndex: 'flowId',
      key: 'flowId',
      render: (flowId: string) => flowId || '-'
    },
    {
      title: 'Plantilla',
      dataIndex: 'messageTemplate',
      key: 'messageTemplate',
      render: (template: string) => (
        <Tooltip title={template}>
          <Typography.Text ellipsis style={{ maxWidth: 200 }}>
            {template}
          </Typography.Text>
        </Tooltip>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditNotification(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteNotification(record.id)}
          />
        </Space>
      )
    }
  ];

  return (
    <>
      <Modal
        title={`${mode === 'edit' ? 'Edit' : 'Create'} Endpoint`}
        open={open}
        onCancel={onClose}
        footer={null}
        maskClosable={false}
        width={800}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            tab={<span><ApiOutlined /> Datos Básicos</span>}
            key="basic"
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Form.Item
                label="ID"
                name="id"
                rules={[{ required: true, message: 'Please input ID' }]}
              >
                <Input disabled={mode === 'edit'} />
              </Form.Item>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Please input name' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Base URL"
                name="baseUrl"
                rules={[{ required: true, message: 'Please input base URL' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Token"
                name="token"
                rules={[{ required: true, message: 'Please input token' }]}
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label="User"
                name="userId"
                rules={[{ required: true, message: 'Please select user' }]}
              >
                <Select>
                  {users.map(user => (
                    <Select.Option key={user.id} value={user.id}>
                      {user.username}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Número de Celular"
                name="celular"
                rules={[
                  {
                    pattern: /^\+?[1-9]\d{1,14}$/,
                    message: 'Ingrese un número de teléfono válido'
                  }
                ]}
              >
                <Input placeholder="Ej: +5491123456789" />
              </Form.Item>
              <Form.Item
                label="Custom Field ID (Gamificación)"
                name="gamificationCustomFieldId"
                tooltip="ID del custom field en Whapify para guardar el mensaje de gamificación. Se usa antes de enviar notificaciones DEPOSIT_GAMIFIED."
              >
                <Input placeholder="Ej: cf_abc123" />
              </Form.Item>
              <Form.Item
                label="Estado"
                name="status"
                initialValue="Active"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Select.Option value="Active">Activo</Select.Option>
                  <Select.Option value="Inactive">Inactivo</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit">
                    {mode === 'create' ? 'Create' : 'Update'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Tabs.TabPane>
          
          {mode === 'edit' && (
            <Tabs.TabPane
              tab={<span><NotificationOutlined /> Notificaciones</span>}
              key="notifications"
            >
              <Card
                title="Configuraciones de Notificación"
                extra={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateNotification}
                  >
                    Nueva Configuración
                  </Button>
                }
              >
                <Table
                  columns={notificationColumns}
                  dataSource={notificationConfigs}
                  rowKey="id"
                  loading={loading}
                  pagination={false}
                  locale={{ emptyText: 'No hay configuraciones de notificación' }}
                />
                
                {notificationConfigs.length === 0 && !loading && (
                  <Typography.Paragraph style={{ textAlign: 'center', marginTop: 16 }}>
                    No hay configuraciones de notificación para este endpoint.
                    <br />
                    Haga clic en "Nueva Configuración" para crear una.
                  </Typography.Paragraph>
                )}
              </Card>
            </Tabs.TabPane>
          )}
        </Tabs>
      </Modal>
      
      {initialData && notificationModalOpen && (
        <NotificationConfigModal
          open={notificationModalOpen}
          onClose={handleNotificationModalClose}
          endpointId={initialData.id}
          initialData={selectedConfig}
          mode={notificationModalMode}
        />
      )}
    </>
  );
};
